"use client";

import { c as _c } from "react/compiler-runtime";
import { IconBlur } from "central-icons/IconBlur";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleX } from "central-icons/IconCircleX";
import { IconColors } from "central-icons/IconColors";
import { IconDownsize } from "central-icons/IconDownsize";
import { IconSquareBehindSquare2 } from "central-icons/IconSquareBehindSquare2";
import { AnimatePresence, m } from "motion/react";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Tooltip } from "@/components/ui/tooltip";
import { useCopyState } from "@/hooks/use-copy-to-clipboard";
import { SPRING_FAST } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { SLIDE_HEIGHT } from "./slide-height";
function CopyButton(t0) {
  const $ = _c(7);
  const {
    children,
    onClick,
    rounded: t1
  } = t0;
  const rounded = t1 === undefined ? false : t1;
  const t2 = rounded ? "rounded-full" : "rounded-xl";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex size-12 items-center justify-center bg-gray-100 shadow-custom transition-colors duration-200 ease-out will-change-transform hover:bg-gray-200", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      scale: 0.95
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== children || $[4] !== onClick || $[5] !== t3) {
    t5 = <m.button className={t3} initial={false} onClick={onClick} transition={SPRING_FAST} type="button" whileTap={t4}>{children}</m.button>;
    $[3] = children;
    $[4] = onClick;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function TooltipBadge(t0) {
  const $ = _c(6);
  const {
    children,
    label,
    className
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex size-7 items-center justify-center rounded-full bg-gray-300 transition-colors duration-200 ease-out hover:bg-gray-400">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== label || $[4] !== t1) {
    t2 = <Tooltip className={className} label={label} render={t1} />;
    $[2] = className;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
const COPY_ICON = <IconSquareBehindSquare2 className="size-7 text-gray-1100 will-change-transform" />;
const CHECK_ICON = <IconCircleCheck className="size-7 text-gray-1100 will-change-transform" />;
const PANELS = [{
  badges: [{
    icon: IconCircleX,
    label: "No Animation"
  }],
  key: "none",
  motion: null
}, {
  badges: [{
    icon: IconColors,
    label: "Opacity"
  }],
  key: "opacity",
  motion: {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1
    }
  }
}, {
  badges: [{
    icon: IconColors,
    label: "Opacity"
  }, {
    icon: IconBlur,
    label: "Blur"
  }, {
    icon: IconDownsize,
    label: "Scale"
  }],
  key: "blur",
  motion: {
    hidden: {
      filter: "blur(4px)",
      opacity: 0,
      scale: 0.25
    },
    visible: {
      filter: "blur(0px)",
      opacity: 1,
      scale: 1
    }
  }
}];
function CopyPanel(t0) {
  const $ = _c(19);
  const {
    panel,
    divided
  } = t0;
  const {
    isCopied,
    markCopied
  } = useCopyState(1000);
  const icon = isCopied ? CHECK_ICON : COPY_ICON;
  const t1 = divided && "border-preview-border border-b sm:border-r sm:border-b-0";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex w-full flex-1 items-center justify-center", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon || $[3] !== isCopied || $[4] !== panel.motion) {
    t3 = panel.motion ? <AnimatePresence initial={false} mode="popLayout"><m.div animate={panel.motion.visible} className="will-change-transform" exit={panel.motion.hidden} initial={panel.motion.hidden} key={isCopied ? "check" : "copy"} transition={SPRING_FAST}>{icon}</m.div></AnimatePresence> : icon;
    $[2] = icon;
    $[3] = isCopied;
    $[4] = panel.motion;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== markCopied || $[7] !== t3) {
    t4 = <CopyButton onClick={markCopied}>{t3}</CopyButton>;
    $[6] = markCopied;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== panel.badges) {
    t5 = panel.badges.map(_temp);
    $[9] = panel.badges;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <div className="absolute -bottom-10 left-1/2 flex -translate-x-1/2 items-center justify-center">{t5}</div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="relative mb-12 flex flex-col items-center justify-center sm:mb-8">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t2 || $[17] !== t7) {
    t8 = <div className={t2}>{t7}</div>;
    $[16] = t2;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function _temp(badge, i) {
  return <TooltipBadge className={cn(i > 0 && "-ml-1 ring-2 ring-preview-bg")} key={badge.label} label={badge.label}><badge.icon className="size-5 text-gray-900" /></TooltipBadge>;
}
export default function AnimateIconsView() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DemoWrapper className="shrink-0" height={SLIDE_HEIGHT}><div className="flex h-full w-full flex-col sm:flex-row">{PANELS.map(_temp2)}</div></DemoWrapper>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(panel, i) {
  return <CopyPanel divided={i < PANELS.length - 1} key={panel.key} panel={panel} />;
}